import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useDashboardFiltersStore } from '@/store/dashboardFiltersStore';
import type { DashboardFilters } from '@/store/dashboardFiltersStore';
import { Badge } from '@/components/ui/badge';
import { useTranslation } from 'react-i18next';
import type { DashboardMetrics } from '../api/dashboard.schemas';

interface PanelGroupProps {
  metrics: DashboardMetrics;
  loading?: boolean;
}

/**
 * PanelGroup Component
 * 
 * Displays key metrics in a responsive grid of cards.
 * Shows total validations, successful, rejected (security), and pending counts.
 */
export function PanelGroup({ metrics, loading = false }: PanelGroupProps) {
  const { t } = useTranslation();
  
  if (loading) {
    return (
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
        {Array.from({ length: 5 }).map((_, index) => (
          <Card key={index}>
            <CardHeader className="pb-2">
              <div className="h-4 bg-muted animate-pulse rounded" />
              <div className="h-3 bg-muted animate-pulse rounded w-2/3" />
            </CardHeader>
            <CardContent>
              <div className="h-8 bg-muted animate-pulse rounded w-1/2" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  const successRate = metrics.total_validations > 0 
    ? Math.round((metrics.successful_validations / metrics.total_validations) * 100)
    : 0;

  const rejectionRate = metrics.total_validations > 0
    ? Math.round((metrics.failed_validations / metrics.total_validations) * 100)
    : 0;

  const activeStatus = useDashboardFiltersStore((s) => s.filters.status);
  const toggleFilter = useDashboardFiltersStore((s) => s.toggleFilter);
  const statusClass = (s: DashboardFilters['status']) =>
    `cursor-pointer transition-colors hover:bg-muted/40 ${
      activeStatus === s ? 'border-primary ring-1 ring-primary/40 bg-primary/5' : ''
    }`;

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">{t('dashboard.totalValidations')}</CardTitle>
          <Badge variant="secondary" className="text-xs">
            {t('dashboard.allTime')}
          </Badge>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{metrics.total_validations.toLocaleString()}</div>
          <p className="text-xs text-muted-foreground">
            {t('dashboard.period')}: {new Date(metrics.period.start).toLocaleDateString()} - {new Date(metrics.period.end).toLocaleDateString()}
          </p>
        </CardContent>
      </Card>

      <Card
        className={statusClass('approved')}
        onClick={() => toggleFilter('status', 'approved')}
        role="button"
        aria-pressed={activeStatus === 'approved'}
      >
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">{t('dashboard.successful')}</CardTitle>
          <Badge variant="default" className="text-xs bg-green-500">
            {successRate}%
          </Badge>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold text-green-600">{metrics.successful_validations.toLocaleString()}</div>
          <p className="text-xs text-muted-foreground">
            {t('dashboard.successRate')}: {metrics.validation_rate.toFixed(1)}%
          </p>
        </CardContent>
      </Card>

      <Card
        className={statusClass('rejected')}
        onClick={() => toggleFilter('status', 'rejected')}
        role="button"
        aria-pressed={activeStatus === 'rejected'}
      >
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">{t('dashboard.rejected')}</CardTitle>
          <Badge variant="destructive" className="text-xs">
            {rejectionRate}%
          </Badge>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold text-red-600">{metrics.failed_validations.toLocaleString()}</div>
          <p className="text-xs text-muted-foreground">
            {t('dashboard.rejectedBySecurity')}
          </p>
        </CardContent>
      </Card>

      <Card
        className={statusClass('pending')}
        onClick={() => toggleFilter('status', 'pending')}
        role="button"
        aria-pressed={activeStatus === 'pending'}
      >
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">{t('dashboard.pending')}</CardTitle>
          <Badge variant="outline" className="text-xs">
            {t('dashboard.current')}
          </Badge>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold text-yellow-600">{metrics.pending_validations.toLocaleString()}</div>
          <p className="text-xs text-muted-foreground">
            {t('dashboard.awaitingProcessing')}
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">{t('dashboard.deviceConversion')}</CardTitle>
          <Badge variant="secondary" className="text-xs">
            {metrics.unique_devices.toLocaleString()} {t('dashboard.devices')}
          </Badge>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold text-blue-600">{metrics.device_conversion_rate.toFixed(1)}%</div>
          <p className="text-xs text-muted-foreground">
            {metrics.approved_devices.toLocaleString()} / {metrics.unique_devices.toLocaleString()} {t('dashboard.devicesApproved')}
          </p>
        </CardContent>
      </Card>
    </div>
  );
}